// src/components/common/aia/context/ChatContext.js
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { chatService } from '../../../../services/chat.service';
import razorpayService from '../../../../services/razorpay.service';

const ChatContext = createContext(null);

const STORAGE_KEY = 'oakhr_chat_messages';
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const ChatProvider = ({ children }) => {
    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [typingStatus, setTypingStatus] = useState('');
    const [aiCreditsTotal, setAiCreditsTotal] = useState(null);
    const [aiCreditsUsed, setAiCreditsUsed] = useState(0);
    const isInitialized = useRef(false);

    // Load AI credit balance from subscription
    const loadCreditBalance = useCallback(async () => {
        try {
            const sub = await razorpayService.getSubscriptionDetails();
            if (sub) {
                setAiCreditsTotal(sub.aiCreditsTotal !== undefined ? sub.aiCreditsTotal : null);
                setAiCreditsUsed(sub.aiCreditsUsed || 0);
            }
        } catch (err) {
            console.error('Error loading AI credits:', err);
        }
    }, []);

    // Load credits on mount
    useEffect(() => {
        loadCreditBalance();
    }, [loadCreditBalance]);

    // Load messages from sessionStorage on mount (instant resume)
    useEffect(() => {
        if (isInitialized.current) return;

        const loadFromStorage = () => {
            try {
                console.log('Loading from storage...');
                const saved = sessionStorage.getItem(STORAGE_KEY);
                console.log('Saved data:', saved);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    console.log('Parsed messages:', parsed.messages);
                    setMessages(parsed.messages || []);
                    setConversationId(parsed.conversationId || null);
                    console.log('Loaded', parsed.messages?.length || 0, 'messages');
                }
            } catch (error) {
                console.error('Error loading from storage:', error);
            }
            isInitialized.current = true;
        };

        loadFromStorage();

        // Also load from backend in background
        loadFromBackend();
    }, []);

    const loadFromBackend = async () => {
        try {
            console.log('Loading from backend...');
            const response = await chatService.getConversations(1, 0);
            console.log('Backend response:', response);

            if (response?.data?.length > 0) {
                const conv = response.data[0];
                console.log('Found conversation:', conv);
                setConversationId(conv.id.toString());

                // Load full conversation with messages
                const fullConv = await chatService.getConversation(conv.id);
                console.log('Full conversation response:', fullConv);

                // Handle nested data structure
                let messagesData = fullConv?.data?.messages;
                if (fullConv?.data?.data?.messages) {
                    messagesData = fullConv.data.data.messages;
                }

                if (messagesData && messagesData.length > 0) {
                    const transformedMessages = messagesData.map(msg => ({
                        id: msg.id?.toString() || generateId(),
                        role: msg.role,
                        content: msg.content,
                        actions: msg.actions || [],
                        timestamp: msg.created_at
                    }));
                    console.log('Setting messages:', transformedMessages);
                    setMessages(transformedMessages);
                    saveToStorage(transformedMessages, conv.id.toString());
                } else if (messages?.length === 0) {
                    // No messages in backend but we have local messages
                    console.log('No backend messages, keeping local messages');
                }
            } else {
                console.log('No conversations found in backend');
            }
        } catch (error) {
            console.error('Error loading from backend:', error);
        }
    };

    const saveToStorage = (msgs, convId) => {
        try {
            const data = {
                messages: msgs,
                conversationId: convId,
                timestamp: new Date().toISOString()
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            console.log('Saved to storage:', msgs.length, 'messages');
        } catch (error) {
            console.error('Error saving to storage:', error);
        }
    };

    // Send message to AI
    const sendMessage = useCallback(async (messageText) => {
        if (!messageText.trim()) return;

        // Generate IDs for both messages
        const userId = generateId();
        const userMessage = {
            id: userId,
            role: 'user',
            content: messageText,
            timestamp: new Date().toISOString()
        };

        // Add user message locally for instant UI
        setMessages(prev => [...prev, userMessage]);
        setIsTyping(true);

        try {
            // Check if chat is enabled
            const chatEnabled = process.env.REACT_APP_AI_CHAT_ENABLED === 'true';

            if (chatEnabled) {
                // Include conversation history for context (before adding new message)
                const recentMessages = messages.slice(-10).map(m => ({
                    role: m.role,
                    content: m.content
                }));

                const response = await chatService.sendMessageWithHistory(messageText, {
                    currentRoute: window.location.pathname,
                    conversationId: conversationId,
                    history: recentMessages
                });

                // Get the conversationId from backend response
                const newConversationId = response.data?.conversationId || conversationId;

                // Update conversationId state
                if (response.data?.conversationId && !conversationId) {
                    setConversationId(newConversationId);
                }

                // Create assistant message
                const assistantMessage = {
                    id: generateId(),
                    role: 'assistant',
                    content: response.data.message,
                    actions: response.data.actions || [],
                    toolCalls: response.data.toolCalls || [],
                    timestamp: new Date().toISOString()
                };

                // Update credit tracking from authoritative server values when
                // available — falling back to optimistic delta only if the server
                // didn't send absolute counters. Using the server's absolute counts
                // prevents the local counter from drifting past total (and going
                // negative) when the limit check blocks further calls.
                if (response.data.creditsUsedTotal !== undefined && response.data.creditsUsedTotal !== null) {
                    setAiCreditsUsed(response.data.creditsUsedTotal);
                } else if (response.data.creditsUsed > 0) {
                    setAiCreditsUsed(prev => prev + response.data.creditsUsed);
                }
                if (response.data.creditsTotal !== undefined) {
                    setAiCreditsTotal(response.data.creditsTotal);
                }

                // Re-sync with the subscription endpoint so the in-memory counter
                // can never drift past the persisted DB value. Fires in the
                // background — doesn't block the message render.
                loadCreditBalance();

                // Add both messages and save to storage with correct conversationId
                setMessages(prev => {
                    const updated = [...prev, assistantMessage];
                    saveToStorage(updated, newConversationId);
                    return updated;
                });
            } else {
                // Demo mode - fallback responses
                await new Promise(resolve => setTimeout(resolve, 1000));
                const { content, actions } = getFallbackResponse(messageText);
                const assistantMessage = {
                    id: generateId(),
                    role: 'assistant',
                    content,
                    actions,
                    timestamp: new Date().toISOString()
                };
                setMessages(prev => {
                    const updated = [...prev, assistantMessage];
                    saveToStorage(updated, conversationId);
                    return updated;
                });
            }
        } catch (error) {
            console.error('Chat error:', error);
            const errorMessage = {
                id: generateId(),
                role: 'assistant',
                content: 'Sorry, I\'m having trouble connecting. Please try again.',
                actions: [],
                timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsTyping(false);
        }
    }, [messages, conversationId]);

    const value = {
        // State
        messages,
        isTyping,
        typingStatus,
        aiCreditsTotal,
        aiCreditsUsed,
        aiCreditsRemaining: aiCreditsTotal !== null ? Math.max(0, aiCreditsTotal - aiCreditsUsed) : null,

        // Actions
        sendMessage,
        refreshCredits: loadCreditBalance,
        clearChat: () => {
            setMessages([]);
            setConversationId(null);
            sessionStorage.removeItem(STORAGE_KEY);
        }
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// Fallback responses for demo mode
const getFallbackResponse = (userMessage) => {
    const lowerMsg = userMessage.toLowerCase();

    if (lowerMsg.includes('leave') || lowerMsg.includes('holiday')) {
        return {
            content: 'To manage leaves, go to Attendance → Leaves in your sidebar. You can apply for leave, check your balance, and view leave history.',
            actions: [{ type: 'navigate', label: 'Go to Leaves', path: '/employee/leaves' }]
        };
    }
    if (lowerMsg.includes('attendance') || lowerMsg.includes('check in') || lowerMsg.includes('check out')) {
        return {
            content: 'You can mark your attendance from the dashboard using the Quick Actions section. Just click Check In when you arrive and Check Out when you leave.',
            actions: [{ type: 'navigate', label: 'Go to Attendance', path: '/employee/attendance' }]
        };
    }
    if (lowerMsg.includes('payslip') || lowerMsg.includes('salary') || lowerMsg.includes('payroll')) {
        return {
            content: 'Access your payslips from the Compensation section in My Access. You can view, download, or print your monthly payslips.',
            actions: [{ type: 'navigate', label: 'View Payslips', path: '/employee/compensation' }]
        };
    }
    if (lowerMsg.includes('tour') || lowerMsg.includes('guide') || lowerMsg.includes('help')) {
        return {
            content: 'Click on my avatar and select a tour from the Tours section to get a guided walkthrough of the available features.',
            actions: []
        };
    }

    return {
        content: "I'm here to help! You can ask me about leaves, attendance, payslips, or any HR-related questions. What would you like to know?",
        actions: []
    };
};

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within ChatProvider');
    }
    return context;
};

export default ChatContext;
